import React, { memo } from 'react';

import { useSelector } from 'react-redux';

import PropTypes from 'prop-types';

import clsx from 'clsx';
import _get from 'lodash/get';

import { useDevice } from '@bonnet/next/device';

import { getScaledImageSource } from '@atc/images';

import {
    DisclaimerText,
    Image,
    Section,
    Text,
} from 'reaxl';
import { useFeatures } from 'reaxl-features';
import {
    OwnerAddress,
    OwnerPhoneNumber,
    OwnerWebsiteCTA,
} from 'reaxl-molecules';
import { KBBDealerRatings } from 'reaxl-ratings-reviews';

import { getDealerDetailUrl } from '@/utilities/ddpUrlBuilder';

import { vdpResultsDuck } from '@/ducks/vdp';

// eslint-disable-next-line import/no-named-default
import { default as OperatingHours } from '@/components/vehicledetails/OperatingHours';
import OwnerContact from '@/components/vehicledetails/OwnerContact/OwnerContact';

const ownerImageSizes = {
    xs: {
        width: 396,
        height: 244,
    },
    sm: {
        width: 353,
        height: 217,
    },
    md: {
        width: 481,
        height: 296,
    },
    lg: {
        width: 407,
        height: 251,
    },
};

const renderMedia = (source, device) => {
    const { src } = source || {};
    const imgSrc = src ? {
        src: getScaledImageSource({
            src,
            bgColor: 'e6e6e6',
            ...ownerImageSizes[device.mediaType],
        }),
        ...ownerImageSizes[device.mediaType],
    } : {
        src: '/content/static/img/placeholders/dealer_fallback.svg',
        'aria-hidden': 'true',
        ...ownerImageSizes[device.mediaType],
    };
    return (
        <Image
            key="dealer-img"
            className={clsx('img-responsive-scale rounded', { 'bg-neutral-tertiary': !src })}
            isResponsive
            {...imgSrc}
            alt=""
        />
    );
};

const getOwnerTitle = (isDdpLinkEnabled, owner, ddpBasePath) => {
    const href = isDdpLinkEnabled ? _get(getDealerDetailUrl(owner, ddpBasePath), 'href', '') : '';
    return href ? (
        <a
            data-cmp="ddpLink"
            className="text-inherit link-undecorated"
            href={href}
        >
            {owner.name}
        </a>
    ) : owner.name;
};

/**
 *  The OwnerDetails component is a component display Owner and Dealership specifications.
 *  If the data is present, it will also display the ratings, hours of operation, Email modal, and website link.
 *
 */

const OwnerDetails = memo(({
    phoneClickHandler,
    ...rest
}) => {

    const {
        ddp_link: [isDdpLinkEnabled, { ddp_base_path: ddpBasePath }],
        disable_dealer_rating: [isDealerRatingDisabled],
    } = useFeatures(['ddp_link', 'disable_dealer_rating']);

    const device = useDevice();
    const isXs = _get(device, 'is.xs', false);
    const isLessThanMd = _get(device, 'lessThan.md', false);
    const listing = useSelector(vdpResultsDuck.selectors.getVdpActiveInventory) || {};
    const { id: inventoryId, owner = {}, marketExtension = {}, ecommWebsite = {} } = listing || {};
    const { id: ownerId, hours, isDealerOpen, images, license, location, phone, rating, languages, website = {} } = owner;
    const { sources = [], primary = 0 } = images || {};
    const { label, href = '' } = website;
    const { isVirtualDealer } = marketExtension;
    const ecommLink = Object.keys(ecommWebsite).length > 1 && !!ecommWebsite?.href;
    const analyticsData = {
        inventoryId,
        ownerId,
    };

    const showDealerRating = !isDealerRatingDisabled && !!rating;

    const hoursAndLanguages = (
        <>
            {!!hours && (
                <div className="margin-top-4">
                    <OperatingHours
                        hours={hours}
                        isDealerOpen={isDealerOpen}
                    />
                </div>
            )}
            {!!languages && (
                <Text
                    data-cmp="ownerLanguages"
                    componentClass="div"
                    color="subdued"
                    className={clsx({ 'margin-top-2': !!hours, 'margin-top-3': !hours })}
                >
                    {languages?.join(', ')}
                    {' '}
                    speaking assistance available
                </Text>
            )}
        </>
    );

    return (
        <div
            className="row"
            data-cmp="ownerDetails"
        >
            <div className="col-xs-12 col-sm-6">
                {renderMedia(sources[primary], device)}
                {!isXs && hoursAndLanguages}
            </div>
            <div className="col-xs-12 col-sm-6">
                <Section
                    alignment="left"
                    heading={getOwnerTitle(isDdpLinkEnabled, owner, ddpBasePath)}
                    headingComponentClass="h2"
                    uiContext="level-3"
                    className="margin-horizontal-2 margin-left-sm-5 margin-horizontal-md-6 margin-top-4 margin-top-sm-0"
                >
                    {showDealerRating && (
                        <KBBDealerRatings
                            className="margin-top-2"
                            owner={owner}
                            widgetType="inline"
                            showCount
                            enableImpression={false}
                        />
                    )}

                    {!!license && (
                        <DisclaimerText className="margin-top-1">
                            Dealer License:
                            {license}
                        </DisclaimerText>
                    )}

                    {!isVirtualDealer && !!location && (
                        <div className={clsx({ 'margin-top-4': showDealerRating }, { 'margin-top-2': !showDealerRating }, 'text-size-300')}>
                            <OwnerAddress
                                location={location}
                                analyticsData={analyticsData}
                            />
                        </div>
                    )}

                    {!!phone && (
                        <div className="margin-top-4 text-size-300">
                            <OwnerPhoneNumber
                                phone={phone}
                                isCallable={isLessThanMd}
                                phoneClickHandler={phoneClickHandler}
                                analyticsData={analyticsData}
                            />
                        </div>
                    )}

                    {isXs && hoursAndLanguages}

                    <hr className="margin-vertical-5" />

                    <OwnerContact
                        analyticsData={analyticsData}
                        phoneClickHandler={phoneClickHandler}
                        {...rest}
                    />

                    {!!href && (
                        <OwnerWebsiteCTA
                            className="margin-top-5"
                            href={href}
                            uiContext="block"
                            label={label}
                            analyticsData={{
                                ctaLocation: 'dealer-detail',
                                label,
                                ecommLink,
                                ...analyticsData,
                            }}
                            bsStyle="primary"
                        />
                    )}
                </Section>
            </div>
        </div>
    );
});

OwnerDetails.propTypes = {
    /**
     * The callback function fires when email CTA is clicked
     */
    onEmailCTAClick: PropTypes.func,
    /**
     * The callback function fires when chat CTA is clicked
     */
    chatClickHandler: PropTypes.func,
    /**
     * The callback function fires when owner phone is called replacing fireTelmetricsTag
     */
    phoneClickHandler: PropTypes.func,
    /**
     * The callback function fires when text CTA is clicked replacing fireTelmetricsTag
     */
    textClickHandler: PropTypes.func,
};

export default OwnerDetails;
